<script setup lang="ts">
// Right-hand showcase panel of the pre-auth shell (sign-up + login).
//
// The photograph is a background layer; the glass cards, headline and dots are
// live DOM on top. That is the whole point of this component: the design ships
// a 708×945 portrait composition, but no real browser window is that tall
// (a MacBook viewport is ~875px, a Windows laptop ~690px). When the text was
// baked into the image, `cover` cropped the headline clean off. As DOM it
// repositions instead, so the panel reads correctly at any viewport.
//
// SCALING — `--u` is one design pixel. It is the SMALLER of the panel's
// width- and height-derived scale, so the overlay shrinks to fit rather than
// overflowing a short, wide panel. Card positions are percentages (see
// `constants/showcaseSlides.ts`), so they stay put while sizes scale.
//
// COLOUR — the rgba values here sit on top of photography, not on a theme
// surface. They must read identically in light and dark mode, so they stay
// literal instead of resolving to `--p-*` tokens.

import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import { SHOWCASE_SLIDES, SHOWCASE_SLIDE_INTERVAL_MS } from '../constants/showcaseSlides'

import iconInvestors from '@assets/images/authImages/icon-investors-lg.svg'
import iconMoneyBag from '@assets/images/authImages/icon-money-bag.svg'
import iconPropertyTag from '@assets/images/authImages/icon-property-tag.svg'
import iconTrendingUp from '@assets/images/authImages/icon-trending-up.svg'

const TILE_ICON = { money: iconMoneyBag, property: iconPropertyTag } as const

// Vite inlines small SVGs as UTF-8 data URIs (`data:image/svg+xml,%3csvg …
// width='26.69' …`), which contain single quotes and parentheses. Inside an
// UNQUOTED `url(...)` that is invalid CSS, so the browser rejects the whole
// declaration and Vue renders no `style` at all — the icons came out blank
// while the larger PNG badges (served as file URLs) worked. Always quote.
// Double quotes are safe: Vite rewrites any `"` in the SVG to `'`.
const toCssUrl = (src: string) => `url("${src}")`

// On the artboard the floating cards all sit above the headline, which starts
// at 693/945. Card positions are normalised against that band and re-projected
// into `.showcase__cards`, so on a short panel the cards compress upward with
// the caption instead of sliding underneath it.
const CARD_BAND = 73.4

const activeIndex = ref(0)
let timerId: ReturnType<typeof setInterval> | undefined

const activeSlide = computed(() => SHOWCASE_SLIDES[activeIndex.value])

const headlineText = computed(() =>
  activeSlide.value.title
    .map((segment) => segment.text.trim())
    .filter(Boolean)
    .join(' '),
)

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const goToSlide = (index: number) => {
  activeIndex.value = index
}

onMounted(() => {
  // An unattended rotating panel is decoration, so reduced-motion parks it on
  // the first slide and lets the dots drive.
  if (prefersReducedMotion() || SHOWCASE_SLIDES.length < 2) return
  timerId = setInterval(() => {
    activeIndex.value = (activeIndex.value + 1) % SHOWCASE_SLIDES.length
  }, SHOWCASE_SLIDE_INTERVAL_MS)
})

onBeforeUnmount(() => {
  if (timerId) clearInterval(timerId)
})
</script>

<template>
  <Box is="aside" class="showcase" :aria-label="`${headlineText}. ${activeSlide.description}`">
    <!-- No `mode="out-in"`: the photos stack absolutely, so the default
         simultaneous mode is what produces an actual crossfade. -->
    <Transition name="showcase-fade">
      <Box
        :key="activeSlide.id"
        class="showcase__photo"
        :style="{ backgroundImage: toCssUrl(activeSlide.photo) }"
      />
    </Transition>

    <Box class="showcase__cards">
      <Box
        v-for="(card, index) in activeSlide.cards"
        :key="`${activeSlide.id}-${index}`"
        class="showcase__card"
        :class="`showcase__card--${card.kind}`"
        :style="{
          left: `${card.x}%`,
          top: `${(card.y / CARD_BAND) * 100}%`,
          backgroundColor: `rgba(255, 255, 255, ${card.fill})`,
        }"
      >
        <!-- Icon + figure -->
        <template v-if="card.kind === 'stat'">
          <Box
            v-if="card.icon === 'investors'"
            class="showcase__stat-icon"
            :style="{ backgroundImage: toCssUrl(iconInvestors) }"
          />
          <Box v-else class="showcase__token">
            <Box class="showcase__token-outer" />
            <Box class="showcase__token-inner" />
          </Box>
          <Text is="span" class="showcase__stat-label">{{ card.label }}</Text>
        </template>

        <!-- Tinted tile, optional delta, figure, caption -->
        <template v-else-if="card.kind === 'metric'">
          <Box
            class="showcase__metric"
            :style="card.width ? { width: `calc(${card.width} * 7.08 * var(--u))` } : undefined"
          >
            <Box class="showcase__metric-top">
              <Box class="showcase__tile">
                <Box
                  class="showcase__tile-icon"
                  :style="{ backgroundImage: toCssUrl(TILE_ICON[card.tile]) }"
                />
              </Box>
              <Box v-if="card.delta" class="showcase__delta">
                <Box
                  class="showcase__delta-icon"
                  :style="{ backgroundImage: toCssUrl(iconTrendingUp) }"
                />
                <Text is="span" class="showcase__delta-text">{{ card.delta }}</Text>
              </Box>
            </Box>
            <Text is="span" class="showcase__metric-value"
              ><CurrencyText :text="card.value"
            /></Text>
            <Text is="span" class="showcase__metric-caption">{{ card.caption }}</Text>
          </Box>
        </template>

        <!-- Partner mark -->
        <Box
          v-else
          class="showcase__logo"
          role="img"
          :aria-label="card.alt"
          :style="{
            backgroundImage: toCssUrl(card.src),
            width: `calc(${card.width} * 7.08 * var(--u))`,
          }"
        />
      </Box>
    </Box>

    <Box class="showcase__caption">
      <Text is="h2" class="showcase__headline">
        <Text
          v-for="(segment, index) in activeSlide.title"
          :key="index"
          is="span"
          :class="[
            segment.accent ? 'showcase__headline-accent' : 'showcase__headline-plain',
            { 'showcase__headline-break': segment.newLine },
          ]"
          ><CurrencyText :text="segment.text"
        /></Text>
      </Text>
      <Text is="p" class="showcase__description"
        ><CurrencyText :text="activeSlide.description"
      /></Text>

      <Box class="showcase__dots">
        <Box
          v-for="(slide, index) in SHOWCASE_SLIDES"
          :key="slide.id"
          is="button"
          type="button"
          class="showcase__dot"
          :class="{ 'showcase__dot--active': index === activeIndex }"
          :aria-label="`Show slide ${index + 1}`"
          :aria-current="index === activeIndex"
          @click="goToSlide(index)"
        />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.showcase {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  align-self: stretch;
  overflow: hidden;
  /* The board draws a 24px corner; `xl` (20px) is the nearest token and the
     radius scale is deliberately capped there. */
  border-radius: var(--p-border-radius-xl);
  background: #0a0817;
  box-sizing: border-box;

  /* One design pixel of the 708×945 artboard.
     · width term leads, keeping the board's proportions against the panel
     · height term is equal to the width term while the ratio is locked, and
       only differs if `max-width` ever clamps the panel
     · the 1px cap stops the overlay growing PAST the design size on a large
       monitor — at 1920 wide it was rendering at 1.42x (62px headline against
       the board's 44px), which is what pushed the text off the panel.
     100/708 = 0.141243 */
  container-type: size;
  --u: min(0.141243cqw, 0.10582cqh, 1px);
}

.showcase__photo {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* ── Glass cards ─────────────────────────────────────────────── */
.showcase__cards {
  position: absolute;
  inset: 0 0 auto 0;
  /* Reserve the caption block rather than guessing a fixed share: two 51px
     headline lines + 12 gap + two 22px description lines + 18 + 10px dots
     = 186 design px, plus its 4.5% bottom offset, plus ~24px of slack because
     a card is positioned by its TOP edge and the tallest one hangs 129px below
     it. At the board size this lands the lowest card within ~2px of Figma; on
     a short panel it compresses the cards upward so they can never slide under
     the headline. */
  height: calc(100% - 4.5% - (210 * var(--u)));
}

.showcase__card {
  position: absolute;
  display: flex;
  align-items: center;
  gap: calc(6.4 * var(--u));
  padding: calc(17 * var(--u)) calc(19 * var(--u));
  border: calc(1.2 * var(--u)) solid rgba(255, 255, 255, 0.3);
  border-radius: calc(18 * var(--u));
  backdrop-filter: blur(calc(10 * var(--u)));
  -webkit-backdrop-filter: blur(calc(10 * var(--u)));
  box-sizing: border-box;
  overflow: hidden;
}

.showcase__card--logo {
  padding: calc(20 * var(--u)) calc(26 * var(--u)) calc(20 * var(--u)) calc(20 * var(--u));
}

.showcase__logo {
  aspect-ratio: 137 / 42;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.showcase__stat-icon {
  width: calc(26.7 * var(--u));
  height: calc(26.7 * var(--u));
  flex-shrink: 0;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

/* Token mark — two rotated squares, as drawn in the board. */
.showcase__token {
  position: relative;
  width: calc(32 * var(--u));
  height: calc(32 * var(--u));
  flex-shrink: 0;
}

.showcase__token-outer,
.showcase__token-inner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-135deg);
}

.showcase__token-outer {
  width: calc(25 * var(--u));
  height: calc(25 * var(--u));
  background: var(--p-primary-600);
  border: calc(1.6 * var(--u)) solid var(--p-surface-200);
  border-radius: calc(9.6 * var(--u));
}

.showcase__token-inner {
  width: calc(6.8 * var(--u));
  height: calc(6.8 * var(--u));
  background: #00bc89;
  border: calc(1.6 * var(--u)) solid #ffffff;
  border-radius: calc(1.6 * var(--u));
}

/* Type inside the panel is a scaled reproduction of a fixed 708px artboard, so
   sizes must track `--u` rather than the fixed `Text` type scale — no preset
   can express "44px at design size, proportionally smaller when the panel
   shrinks". Documented exception to the no-font-size-in-scoped-CSS rule. */
.showcase__stat-label {
  font-size: calc(21.4 * var(--u));
  font-weight: 700;
  line-height: 1.5;
  color: #ffffff;
  white-space: nowrap;
}

/* ── Metric card ─────────────────────────────────────────────── */
.showcase__metric {
  display: flex;
  flex-direction: column;
  gap: calc(2 * var(--u));
}

.showcase__metric-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: calc(10 * var(--u));
  margin-bottom: calc(3 * var(--u));
}

.showcase__tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(34 * var(--u));
  height: calc(34 * var(--u));
  flex-shrink: 0;
  border-radius: calc(11.3 * var(--u));
  background: rgba(253, 242, 249, 0.7);
}

.showcase__tile-icon {
  width: calc(18.4 * var(--u));
  height: calc(18.4 * var(--u));
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.showcase__delta {
  display: flex;
  align-items: center;
  gap: calc(2.6 * var(--u));
  padding: calc(2 * var(--u)) calc(5 * var(--u));
  border: calc(1 * var(--u)) solid #dbdbdb;
  border-radius: calc(10 * var(--u));
  background: rgba(255, 255, 255, 0.84);
  flex-shrink: 0;
}

.showcase__delta-icon {
  width: calc(10 * var(--u));
  height: calc(10 * var(--u));
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.showcase__delta-text {
  font-size: calc(12 * var(--u));
  font-weight: 500;
  line-height: 1;
  color: var(--p-success-700);
  white-space: nowrap;
}

.showcase__metric-value {
  font-size: calc(21.4 * var(--u));
  font-weight: 700;
  line-height: 1.5;
  color: #ffffff;
  white-space: nowrap;
}

.showcase__metric-caption {
  font-size: calc(16 * var(--u));
  font-weight: 500;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.86);
  white-space: nowrap;
}

/* ── Headline block ──────────────────────────────────────────── */
.showcase__caption {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  /* The board ends the block ~40px above the artboard base (905/945), so
     anchoring from the bottom keeps it on-panel however short the panel gets,
     instead of being pushed off by a top offset. */
  bottom: 4.5%;
  /* 613/708 of the board, but never wider than the board drew it. */
  width: min(86.6%, calc(613 * var(--u)));
  display: flex;
  flex-direction: column;
  gap: calc(12 * var(--u));
}

.showcase__headline {
  /* Inline flow, so a long headline wraps like the board's does. Segments
     only break where the design explicitly breaks them. */
  display: block;
  margin: 0;
  font-family: var(--p-heading-font-family);
  font-size: calc(44 * var(--u));
  font-weight: 700;
  line-height: 1.16;
  letter-spacing: calc(-0.88 * var(--u));
}

.showcase__headline-plain {
  color: #ffffff;
}

.showcase__headline-break {
  display: block;
}

.showcase__headline-accent {
  color: var(--p-primary-500);
}

.showcase__description {
  margin: 0;
  font-size: calc(16 * var(--u));
  font-weight: 400;
  /* 22px at design size (board: 16/22). */
  line-height: 1.375;
  color: rgba(255, 255, 255, 0.72);
}

/* ── Dots ────────────────────────────────────────────────────── */
.showcase__dots {
  display: flex;
  align-items: center;
  gap: calc(4 * var(--u));
  margin-top: calc(18 * var(--u));
}

.showcase__dot {
  width: calc(20 * var(--u));
  height: calc(10 * var(--u));
  padding: 0;
  border: none;
  border-radius: calc(6 * var(--u));
  background: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition:
    width 0.25s ease,
    background-color 0.25s ease;
}

.showcase__dot--active {
  width: calc(34 * var(--u));
  background: #ffffff;
}

.showcase__dot:focus-visible {
  outline: calc(2 * var(--u)) solid #ffffff;
  outline-offset: calc(2 * var(--u));
}

/* ── Slide crossfade ─────────────────────────────────────────── */
.showcase-fade-enter-active,
.showcase-fade-leave-active {
  transition: opacity 500ms ease;
}

.showcase-fade-enter-from,
.showcase-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .showcase-fade-enter-active,
  .showcase-fade-leave-active,
  .showcase__dot {
    transition: none;
  }
}
</style>
